/**
 * Bildschirmfoto der eigenen Oberfläche. (28.09.2026, Ticket 2680)
 *
 * Martin, 26.09.: den gerade sichtbaren Vespera-Bildschirm an den Chat geben,
 * „ohne den Umweg über Fotos-App und manuellen Upload“.
 *
 * NUR die eigene Seite: Das Bild entsteht aus dem DOM dieser Seite, nicht
 * aus dem Bildschirm des Geräts. Fremde Inhalte (iframe, Video) bleiben leere
 * Flächen, Passwortfelder bleiben unsichtbar (istGeheim).
 *
 * Der Weg: die sichtbaren Elemente klonen, ihre BERECHNETEN Stile als Klassen
 * in ein SVG mit <foreignObject> schreiben, Bilder und Schriften als data:-
 * Adressen einbetten, das SVG auf eine Leinwand zeichnen.
 *
 * ⚠️ Berechnete Stile statt style.css: style.css hat 1,2 MB und hängt an
 *    :root[data-mode] und [data-palette]. Im SVG ist :root das <svg>, keine
 *    dieser Regeln träfe. Die berechneten Werte sind schon aufgelöst.
 * ⚠️ Die Stile stehen im <style> DES BILDES, nie in der Seite — der Klon
 *    kommt nie ins DOM der App.
 * ⚠️ Keine Bibliothek: node_modules gehört root, eine neue Abhängigkeit ließ
 *    sich am 28.09. nicht einbauen. html2canvas käme ohnehin nicht in Frage,
 *    es liest color-mix() nicht, und style.css nutzt es hunderte Male.
 */

const XHTML = "http://www.w3.org/1999/xhtml";
const SVG_NS = "http://www.w3.org/2000/svg";

/** Fremder Inhalt: die Fläche bleibt, der Inhalt nicht. */
const NUR_FLAECHE = new Set(["iframe", "video", "audio", "embed", "object"]);
/** Diese Merkmale lädt, versteckt oder färbt der Klon selbst. */
const OHNE_MERKMAL = new Set(["class", "style", "hidden", "src", "srcset", "sizes", "loading", "decoding"]);
/** Zeichen, die XML nicht erlaubt — ein einziges macht das ganze Bild kaputt. */
// eslint-disable-next-line no-control-regex -- genau diese Zeichen sind gemeint
const STEUERZEICHEN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g;
const XML_NAME = /^[A-Za-z_][\w.:-]*$/;
/** url(...) in einem berechneten Wert, auch mit Klammern im Anführungstext. */
const URL_IN_CSS = /url\(\s*(?:"((?:[^"\\]|\\.)*)"|'((?:[^'\\]|\\.)*)'|([^"')\s]+))\s*\)/g;

/** Ein Feld, dessen Inhalt nie ins Bild darf. */
export function istGeheim(el: Element): boolean {
  if (el.localName !== "input") return false;
  const typ = (el.getAttribute("type") ?? "").toLowerCase();
  return typ === "password" || /password/i.test(el.getAttribute("autocomplete") ?? "");
}

/** Bewegung friert das Bild ein: Es zählt der Wert von jetzt, nicht der Ablauf. */
function zaehlt(name: string): boolean {
  return !name.startsWith("--") && !name.startsWith("animation") && !name.startsWith("transition");
}

/*
 * Grundwerte je Element-Art, gemessen in einem leeren Rahmen. Geschrieben
 * wird nur, was davon ODER vom Elternteil abweicht — sonst trüge jedes
 * Element rund 400 Angaben. Stimmt ein Wert mit beiden überein, ergibt er
 * sich im Bild von selbst: geerbt vom Elternteil oder als Grundwert.
 */
const GRUNDWERTE = new Map<string, Map<string, string>>();

class Vorlage {
  rahmen: HTMLIFrameElement | null = null;
  constructor(private readonly doc: Document) {}

  werte(ns: string, name: string): Map<string, string> {
    const schluessel = `${ns}|${name}`;
    const bekannt = GRUNDWERTE.get(schluessel);
    if (bekannt) return bekannt;
    const leer = this.leererRahmen();
    if (!leer) return new Map();
    const probe = leer.doc.createElementNS(ns, name);
    let ort: Element = leer.doc.body;
    if (ns === SVG_NS && name !== "svg") {
      ort = leer.doc.createElementNS(SVG_NS, "svg");
      leer.doc.body.appendChild(ort);
    }
    ort.appendChild(probe);
    const stil = leer.win.getComputedStyle(probe);
    const werte = new Map<string, string>();
    for (let i = 0; i < stil.length; i++) {
      const p = stil.item(i);
      werte.set(p, stil.getPropertyValue(p));
    }
    leer.doc.body.replaceChildren();
    GRUNDWERTE.set(schluessel, werte);
    return werte;
  }

  private leererRahmen(): { doc: Document; win: Window } | null {
    if (!this.rahmen) {
      const f = this.doc.createElement("iframe");
      f.className = "bildschirmfoto-vorlage";
      f.setAttribute("aria-hidden", "true");
      f.tabIndex = -1;
      this.doc.body.appendChild(f);
      this.rahmen = f;
      const d = f.contentDocument;
      if (d) {
        d.open();
        d.write("<!DOCTYPE html><html><head></head><body></body></html>");
        d.close();
      }
    }
    const win = this.rahmen.contentWindow;
    const doc = this.rahmen.contentDocument;
    return win && doc?.body ? { doc, win } : null;
  }

  entsorgen(): void {
    this.rahmen?.remove();
    this.rahmen = null;
  }
}

interface Lauf {
  win: Window;
  doc: Document;
  breite: number;
  hoehe: number;
  vorlage: Vorlage;
  klassen: Map<string, string>;
  regeln: string[];
  bilder: Array<{ el: Element; merkmal: "src" | "href"; adresse: string }>;
}

function erklaerung(
  stil: CSSStyleDeclaration,
  grund: Map<string, string>,
  eltern: CSSStyleDeclaration | null,
): string[] {
  const teile: string[] = [];
  for (let i = 0; i < stil.length; i++) {
    const name = stil.item(i);
    if (!zaehlt(name)) continue;
    const wert = stil.getPropertyValue(name);
    if (wert === "") continue;
    if (wert === grund.get(name) && (eltern === null || eltern.getPropertyValue(name) === wert)) {
      continue;
    }
    teile.push(`${name}:${wert}`);
  }
  return teile;
}

/** Gleiche Stile, eine Klasse — ein Gespräch hat hunderte gleicher Blasen. */
function klasse(lauf: Lauf, auswahl: "" | "::before" | "::after", teile: string[]): string {
  const text = teile.join(";");
  const schluessel = `${auswahl}{${text}`;
  let k = lauf.klassen.get(schluessel);
  if (!k) {
    k = `bf${lauf.klassen.size}`;
    lauf.klassen.set(schluessel, k);
    lauf.regeln.push(`.${k}${auswahl}{${text}}`);
  }
  return k;
}

/*
 * Ein Klon kennt seine Rollposition nicht — er stünde wieder ganz oben.
 * Darum rücken die Kinder eines gerollten Elements um die Rollweite zurück.
 * Festgesteckte (fixed, sticky) bleiben, wo sie sind.
 */
function verschoben(wert: string, [x, y]: [number, number]): string {
  if (!wert || wert === "none") return `translate:${-x}px ${-y}px`;
  const [a, b = "0px", ...rest] = wert.split(" ");
  return `translate:calc(${a} - ${x}px) calc(${b} - ${y}px)${rest.length ? ` ${rest.join(" ")}` : ""}`;
}

function neuesElement(lauf: Lauf, el: Element): Element {
  const name = el.localName;
  if (NUR_FLAECHE.has(name)) return lauf.doc.createElement("div");
  if (name === "canvas") {
    const bild = lauf.doc.createElement("img");
    try {
      bild.setAttribute("src", (el as HTMLCanvasElement).toDataURL());
    } catch {
      // Eine Leinwand mit fremdem Inhalt ist gesperrt — dann bleibt sie leer.
    }
    return bild;
  }
  const k = lauf.doc.createElementNS(el.namespaceURI ?? XHTML, name);
  for (const a of Array.from(el.attributes)) {
    if (OHNE_MERKMAL.has(a.name) || /^(on|xmlns(:|$))/i.test(a.name) || !XML_NAME.test(a.name)) {
      continue;
    }
    if (name === "image" && /(^|:)href$/.test(a.name)) continue;
    k.setAttributeNS(a.namespaceURI, a.name, a.value);
  }
  if (name === "img") {
    const img = el as HTMLImageElement;
    // currentSrc ist "" (nicht null), solange nichts geladen ist — darum ||.
    const adresse = img.currentSrc || (img.getAttribute("src") ?? "");
    if (adresse) lauf.bilder.push({ el: k, merkmal: "src", adresse });
  } else if (name === "image") {
    const adresse = el.getAttribute("href") ?? el.getAttribute("xlink:href") ?? "";
    if (adresse) lauf.bilder.push({ el: k, merkmal: "href", adresse });
  } else if (name === "input") {
    const feld = el as HTMLInputElement;
    k.removeAttribute("value");
    k.removeAttribute("checked");
    if (feld.type === "checkbox" || feld.type === "radio") {
      if (feld.checked) k.setAttribute("checked", "");
    } else if (!istGeheim(el)) {
      k.setAttribute("value", feld.value);
    }
  } else if (name === "textarea") {
    k.textContent = (el as HTMLTextAreaElement).value.replace(STEUERZEICHEN, "");
  } else if (name === "option") {
    if ((el as HTMLOptionElement).selected) k.setAttribute("selected", "");
    else k.removeAttribute("selected");
  }
  return k;
}

/**
 * Kästen, deren Maß ohne Inhalt gleich bleibt, sobald Breite und Höhe fest
 * stehen: die blockartigen. Ein Stück Fließtext (inline, inline-block) hängt
 * an seiner Zeile — leer geklont verschöbe es die Zeilen dahinter.
 */
const BLOCKKASTEN = new Set(["block", "flow-root", "flex", "grid", "list-item", "table"]);
/** Behälter, deren Kinder Flex-Glieder sind. */
const FLEX = new Set(["flex", "inline-flex"]);

/*
 * Ganz außerhalb des Fensters zählt der Inhalt nicht — ein langes Gespräch
 * hat tausende Elemente, die niemand sieht. Die Hülle bleibt, damit alles
 * Sichtbare an seiner Stelle steht.
 */
function nurHuelle(el: Element, stil: CSSStyleDeclaration, lauf: Lauf): boolean {
  if (stil.position === "fixed" || !BLOCKKASTEN.has(stil.display)) return false;
  const r = el.getBoundingClientRect();
  return r.bottom < 0 || r.right < 0 || r.top > lauf.hoehe || r.left > lauf.breite;
}

function klone(
  quelle: Node,
  lauf: Lauf,
  eltern: CSSStyleDeclaration | null,
  gerollt: [number, number] | null,
): Node | null {
  if (quelle.nodeType === 3) {
    return lauf.doc.createTextNode((quelle.nodeValue ?? "").replace(STEUERZEICHEN, ""));
  }
  if (quelle.nodeType !== 1) return null;
  const el = quelle as Element;
  const name = el.localName;
  if (el === lauf.vorlage.rahmen) return null;
  // EINE Regel für „nicht zu sehen“: head, script, style, [hidden] und alles,
  // was die App ausblendet, hat display:none.
  const stil = lauf.win.getComputedStyle(el);
  if (stil.display === "none") return null;

  const k = neuesElement(lauf, el);
  const teile = erklaerung(stil, lauf.vorlage.werte(k.namespaceURI ?? XHTML, k.localName), eltern);
  if (gerollt && stil.position !== "fixed" && stil.position !== "sticky") {
    teile.push(verschoben(stil.getPropertyValue("translate"), gerollt));
  }
  if (istGeheim(el)) teile.push("visibility:hidden");
  /*
   * Ein Flex-Glied behält sein gemessenes Maß — Breite und Höhe stehen schon
   * in `teile`, das Glied darf sie nur nicht mehr verändern. (Nachbesserung
   * 28.09.2026) Sonst rechnet das Bild den Flex-Ausgleich neu, und zwar
   * anders: Mit fester Höhe ist die Mindesthöhe der Inhalt, nicht mehr die
   * Höhe — im vollen Gespräch (.chat ist eine Flex-Spalte) schrumpfte jede
   * Blase um den Außenabstand ihres letzten Absatzes, eine leere Hülle
   * außerhalb des Fensters sogar auf 0. Die sichtbaren Blasen rückten so um
   * die Rollweite aus dem Bild.
   */
  if (eltern && FLEX.has(eltern.display)) teile.push("flex:none");
  const klassen = [klasse(lauf, "", teile)];
  if (el.namespaceURI === XHTML && !NUR_FLAECHE.has(name)) {
    for (const auswahl of ["::before", "::after"] as const) {
      const ps = lauf.win.getComputedStyle(el, auswahl);
      const inhalt = ps.getPropertyValue("content");
      if (!inhalt || inhalt === "none" || inhalt === "normal" || ps.display === "none") continue;
      klassen.push(klasse(lauf, auswahl, erklaerung(ps, lauf.vorlage.werte(XHTML, "span"), stil)));
    }
  }
  k.setAttribute("class", klassen.join(" "));

  if (NUR_FLAECHE.has(name) || name === "canvas" || name === "textarea") return k;
  if (nurHuelle(el, stil, lauf)) return k;
  const rolle: [number, number] | null =
    el.scrollLeft || el.scrollTop ? [el.scrollLeft, el.scrollTop] : null;
  for (const kind of Array.from(el.childNodes)) {
    const kk = klone(kind, lauf, stil, rolle);
    if (kk) k.appendChild(kk);
  }
  return k;
}

async function alsDatenAdresse(adresse: string): Promise<string | null> {
  const ac = new AbortController();
  const frist = setTimeout(() => ac.abort(), 8000);
  try {
    const antwort = await fetch(adresse, { credentials: "same-origin", signal: ac.signal });
    if (!antwort.ok) return null;
    const blob = await antwort.blob();
    return await new Promise<string | null>((fertig) => {
      const leser = new FileReader();
      leser.onload = () => fertig(typeof leser.result === "string" ? leser.result : null);
      leser.onerror = () => fertig(null);
      leser.readAsDataURL(blob);
    });
  } catch {
    return null;
  } finally {
    clearTimeout(frist);
  }
}

/**
 * Jede url(...) im CSS als data:-Adresse — ein Bild im Bild lädt nichts nach.
 * Ein Verweis in diese Seite (url(#verlauf)) wird wieder zum Verweis ins SVG.
 */
async function adressenEinbetten(
  css: string,
  basis: string,
  seite: string,
): Promise<{ text: string; fehlend: number }> {
  const adressen = new Set<string>();
  for (const m of css.matchAll(URL_IN_CSS)) {
    const u = m[1] ?? m[2] ?? m[3] ?? "";
    if (u && !u.startsWith("data:") && !u.startsWith("#")) adressen.add(u);
  }
  const ersatz = new Map<string, string>();
  let fehlend = 0;
  await Promise.all(
    [...adressen].map(async (u) => {
      const voll = new URL(u, basis);
      if (voll.hash && voll.href.split("#")[0] === seite) {
        ersatz.set(u, voll.hash);
        return;
      }
      const daten = await alsDatenAdresse(voll.href);
      if (daten) ersatz.set(u, daten);
      else fehlend++;
    }),
  );
  const text = css.replace(URL_IN_CSS, (ganz, a?: string, b?: string, c?: string) => {
    const neu = ersatz.get(a ?? b ?? c ?? "");
    return neu ? `url("${neu}")` : ganz;
  });
  return { text, fehlend };
}

/** Die Schriften ändern sich nicht — einmal vollständig eingebettet, gilt es. */
let schriftVorrat: string | null = null;

async function schriftRegeln(doc: Document, seite: string): Promise<string> {
  if (schriftVorrat !== null) return schriftVorrat;
  const teile = await Promise.all(
    Array.from(doc.styleSheets).flatMap((blatt) => {
      let liste: CSSRule[];
      try {
        liste = Array.from(blatt.cssRules);
      } catch {
        return []; // fremdes Blatt: gesperrt, und nicht unsere Schrift
      }
      return liste
        .filter((r) => r.cssText.startsWith("@font-face"))
        .map((r) => adressenEinbetten(r.cssText, blatt.href ?? doc.baseURI, seite));
    }),
  );
  const text = teile.map((t) => t.text).join("");
  if (teile.every((t) => t.fehlend === 0)) schriftVorrat = text;
  return text;
}

/** Der Seitengrund — im echten Fenster füllt er auch, was unter dem <body> frei bleibt. */
function grundfarbe(win: Window): string {
  for (const el of [win.document.documentElement, win.document.body]) {
    if (!el) continue;
    const f = win.getComputedStyle(el).backgroundColor;
    if (f && f !== "transparent" && f.replace(/\s/g, "") !== "rgba(0,0,0,0)") return f;
  }
  return "#ffffff";
}

/*
 * WebKit (Safari, die App, Chrome am iPhone) zeichnet eingebettete Bilder
 * im SVG erst beim zweiten oder dritten Mal. Darum dort mehrmals zeichnen.
 */
function mehrmalsZeichnen(win: Window): boolean {
  const ua = win.navigator.userAgent;
  return /AppleWebKit/.test(ua) && !/Chrome\//.test(ua);
}

/** Ortszeit im Dateinamen — so, wie Martin die Uhr sieht. */
function stempel(d: Date): string {
  const z = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}-${z(d.getHours())}${z(d.getMinutes())}${z(d.getSeconds())}`;
}

export interface Abbild {
  /** Das SVG mit dem Klon. Bilder und Schriften kommen erst mit `alsDatei` hinein. */
  readonly svg: SVGSVGElement;
  alsDatei(): Promise<File>;
}

/**
 * Nimmt den sichtbaren Bildschirm JETZT auf — der Klon entsteht sofort, was
 * danach erscheint (etwa eine Meldung), steht nicht mehr im Bild.
 */
export function baueAbbild(win: Window = window): Abbild {
  const doc = win.document;
  const breite = Math.max(1, Math.round(win.innerWidth));
  const hoehe = Math.max(1, Math.round(win.innerHeight));
  const skala = Math.min(2, Math.max(1, win.devicePixelRatio || 1));
  const seite = doc.URL.split("#")[0];
  const lauf: Lauf = {
    win, doc, breite, hoehe,
    vorlage: new Vorlage(doc),
    klassen: new Map(),
    regeln: [],
    bilder: [],
  };
  let wurzel: Node | null;
  try {
    wurzel = klone(doc.documentElement, lauf, null, null);
  } finally {
    lauf.vorlage.entsorgen();
  }
  const svg = doc.createElementNS(SVG_NS, "svg");
  svg.setAttribute("width", String(Math.round(breite * skala)));
  svg.setAttribute("height", String(Math.round(hoehe * skala)));
  svg.setAttribute("viewBox", `0 0 ${breite} ${hoehe}`);
  const stilblatt = doc.createElementNS(SVG_NS, "style");
  stilblatt.textContent = lauf.regeln.join("");
  svg.appendChild(stilblatt);
  const fremd = doc.createElementNS(SVG_NS, "foreignObject");
  fremd.setAttribute("x", "0");
  fremd.setAttribute("y", "0");
  fremd.setAttribute("width", String(breite));
  fremd.setAttribute("height", String(hoehe));
  if (wurzel) fremd.appendChild(wurzel);
  svg.appendChild(fremd);
  const grund = grundfarbe(win);

  return {
    svg,
    async alsDatei(): Promise<File> {
      const vorrat = new Map<string, Promise<string | null>>();
      const holen = (u: string): Promise<string | null> => {
        const voll = new URL(u, doc.baseURI).href;
        let p = vorrat.get(voll);
        if (!p) {
          p = voll.startsWith("data:") ? Promise.resolve(voll) : alsDatenAdresse(voll);
          vorrat.set(voll, p);
        }
        return p;
      };
      const [schriften, stile] = await Promise.all([
        schriftRegeln(doc, seite),
        adressenEinbetten(lauf.regeln.join(""), doc.baseURI, seite),
        Promise.all(
          lauf.bilder.map(async (b) => {
            const daten = await holen(b.adresse);
            if (daten) b.el.setAttribute(b.merkmal, daten);
            else b.el.setAttribute("alt", "");
          }),
        ),
      ]);
      stilblatt.textContent = schriften + stile.text;
      const text = new XMLSerializer().serializeToString(svg);

      const bild = new Image();
      await new Promise<void>((fertig, fehler) => {
        bild.onload = () => fertig();
        bild.onerror = () => fehler(new Error("Das Abbild ließ sich nicht zeichnen."));
        bild.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(text)}`;
      });
      const leinwand = doc.createElement("canvas");
      leinwand.width = Math.round(breite * skala);
      leinwand.height = Math.round(hoehe * skala);
      const ctx = leinwand.getContext("2d");
      if (!ctx) throw new Error("Keine Leinwand zum Zeichnen.");
      const zeichnen = (): void => {
        ctx.fillStyle = grund;
        ctx.fillRect(0, 0, leinwand.width, leinwand.height);
        ctx.drawImage(bild, 0, 0, leinwand.width, leinwand.height);
      };
      zeichnen();
      if (mehrmalsZeichnen(win)) {
        const mal = Math.min(8, 1 + lauf.bilder.length);
        for (let i = 0; i < mal; i++) {
          await new Promise((weiter) => setTimeout(weiter, 100));
          zeichnen();
        }
      }
      const blob = await new Promise<Blob | null>((fertig) => leinwand.toBlob(fertig, "image/png"));
      if (!blob) throw new Error("Das Bild ließ sich nicht speichern.");
      return new File([blob], `bildschirmfoto-${stempel(new Date())}.png`, { type: "image/png" });
    },
  };
}
